import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import { logout } from "../../redux/authSlice";
import {
  ArrowRight, Menu, X, LogOut, LogIn, MapPin, Search, ChevronDown,
  ChevronLeft, ChevronRight, Smartphone, User, Cloud, LifeBuoy,
  Home as HomeIcon, Building2, Store, Sparkles, Heart,
} from "lucide-react";

import logoImg from "../../assets/app/logo.png";
import appLogo from "../../assets/app/logo.png";
import AppleLogo from "../../assets/svg/apple.svg";
import GoogleLogo from "../../assets/svg/google.svg";

/* ═══════════════════════════════════════════════════════════
   LANDING DATA (logged OUT — Heartlas look) — same source UIHome uses
═══════════════════════════════════════════════════════════ */
import {
  Hero,
  App as APP_CATEGORIES,
  Network as NETWORK_CARDS,
  FAQs as FAQ_ITEMS,
  CategoryItem as UICategoryItem,
  FAQItem,
} from "../../CATEGORIES/UICategories";
import SmartFooter from "../../GLOBAL/SmartFooter";

/* ═══════════════════════════════════════════════════════════
   APP DATA (logged IN — Atlas shell) — single source of truth
═══════════════════════════════════════════════════════════ */
import {
  appCategories,
  getAppSubcategories,
  renderAppCategoryVisual,
  type CategoryItem,
} from "../../CATEGORIES/AppCategories";

/* ═══════════════════════════════════════════════════════════
   ⭐⭐⭐ SUBPAGE IMPORTS — THE MASTER TOGGLE ZONE ⭐⭐⭐
   ────────────────────────────────────────────────────────
   Comment an import out (//) AND its line in SCREEN_COMPONENTS
   below to hide that screen. Remove the // on both to show it.
   Nothing else in the file needs to change.
═══════════════════════════════════════════════════════════ */
import StoreProductsHome from "../../SUPERAPP/ADMINISTRATION/SELLERCENTRAL/StoreProductsHome";
import VacationRentalsHome from "../BOOKING/VACATIONRENTALS/VacationRentals";
import CleaningHome from "../../SUPERAPP/BOOKING/CLEANING/DASHBOARD/CleaningHome";
import RiderHome from "../../SUPERAPP/BOOKING/RIDE/RiderHome";
// import GroceriesHome from "../../SUPERAPP/SHOPPING/GROCERIES/GroceriesHome";
// import FastHome from "../SHOPPING/FASTFOOD/FastHome";
// import FashionHome from "../SHOPPING/FashionHome";
// import ElectronicsHome from "../../SUPERAPP/SHOPPING/ELECTRONICS/ElectronicsHome";
// import BabyHome from "../SHOPPING/BabyHome";
// import BeautyHome from "../SHOPPING/BeautyHome";
// import BagsHome from "../SHOPPING/BagsHome";
// import BooksHome from "../SHOPPING/BooksHome";
// import FurnitureHome from "../SHOPPING/FurnitureHome";
// import KitchenHome from "../SHOPPING/KitchenHome";
// import AutomotiveHome from "../SHOPPING/AutomotiveHome";
// import FarmHome from "../SHOPPING/FarmHome";
// import TicketsHome from "../BOOKING/TICKETS/TicketsHome";
// import AppointmentHome from "../BOOKING/APPOINTMENT/AppointmentHome";
// import ClientProductOrders from "../CLIENTDASHBOARD/ClientProductOrders";
// import ShortBookingsDashboard from "../BOOKING/VACATIONRENTALS/ShortBookings/ShortBookingsDashboard";

// ── Chat panel (same one FoodDashboard/AtlasHome use) ──
import IMessagePreview from "../../MESSAGES/IMessagePreview";
import FreshStoreHome from "./FRESHCENTRAL/FreshStoreHome";
import ElectronicsStore from "./SELLERCENTRAL/STORES/ElectronicsStore";
import BeautyStore from "./SELLERCENTRAL/STORES/BeautyStore";
import ApparelStore from "./SELLERCENTRAL/STORES/ApparelStore";
import BabyStore from "./SELLERCENTRAL/STORES/BabyStore";
import FurnitureStore from "./SELLERCENTRAL/STORES/FurnitureStore";
import GroceryStore from "./FRESHCENTRAL/GroceryStore";
import KitchenStore from "./SELLERCENTRAL/STORES/KitchenStore";
import LuggageStore from "./SELLERCENTRAL/STORES/LuggageStore";
import StoreBuyerOrders from "./SELLERCENTRAL/BUYERCENTRAL/StoreBuyerOrders";
import FreshBuyerOrders from "./FRESHCENTRAL/FRESHBUYERCENTRAL/FreshBuyerOrders";
import FoodStoreHome from "./FRESHCENTRAL/FoodStoreHome";
import HeartGuide from "SUPERAPP/GUIDE/HeartGuide";

/* ═══════════════════════════════════════════════════════════
   SUBCATEGORY ID → COMPONENT
   One line per screen. // a line to hide it; the sidebar / tab
   bar still lists the subcategory (from AppCategories), it just
   won't open until you un-comment its line + import.
═══════════════════════════════════════════════════════════ */
const SCREEN_COMPONENTS: Record<number, React.FC<any>> = {
  
  301: VacationRentalsHome,      // Vacation Rentals
  304: CleaningHome,             // Cleaners
  308: RiderHome,                // Ride

  // ── flip these on by removing "//" here AND on the matching import above ──
 // ── flip these on by removing "//" here AND on the matching import above ──
  201: FreshStoreHome,               // Fast Food (Retained from FreshStoreHome)
  202: GroceryStore,           // Groceries
  203: FoodStoreHome,                // Farm
  204: StoreProductsHome,            // Products (Retained from StoreProductsHome)
  205: ApparelStore,             // Fashion (Retained from ApparelStore)
  206: ElectronicsStore,         // Electronics (Retained from ElectronicsStore)
  207: BabyStore,                // Baby & Maternity (Retained from BabyStore)
  208: BeautyStore,              // Beauty (Retained from BeautyStore)
  209: LuggageStore,                // Bags
  //210: BooksStore,               // Books
  211: FurnitureStore,           // Furniture (Retained from FurnitureStore)
  212: KitchenStore,             // Kitchen
  //213: AutomotiveStore,          // Automotive
  //214: ArtCraftsStore,           // Art & Crafts
  //215: AviationStore,            // Aviation
  //216: GamingStore,              // Gaming
  //217: GiftsStore,               // Gifts
  //218: PetsStore,                // Pets
  //219: SportsStore,              // Sports
  //220: ToolsStore,               // Tools
  //221: ToysStore,                // Toys

  


  // 306: TicketsHome,           // Tickets
  // 307: AppointmentHome,       // Appointment
   401: StoreBuyerOrders,   // Orders
    403: FreshBuyerOrders,   // Orders
  // 402: ShortBookingsDashboard,// Trips

   601: HeartGuide,
};

/* ── which screen greets a signed-in user first ── */
const DEFAULT_ACTIVE_SUBCATEGORY_ID = 202; // Products

const HOME_CATEGORY_ID = 1;
const SHOPPING_CATEGORY_ID = 2;
const ORDERS_CATEGORY_ID = 4;
const CHAT_CATEGORY_ID = 5;

const ROUTES = {
  home: "/",
  profile: (userId?: string) => `/profile/${userId ?? ""}`,
  support: (userId?: string) => `/CustomerSupport/${userId ?? ""}`,
  signIn: "/AuthSwitch",
};

/* ─── Heartlas product navigation mapping (landing) ─── */
const PRODUCT_ROUTES: Record<string, string> = {
  "Vacation Rentals": "/vacationrentals",
  "Cleaners & Laundromats": "/cleaners",
  "Offices": "/offices",
  "Furniture": "/furniture",
  "Vacant Rentals": "/vacant",
  "Property for Sale": "/property",
};

const HERO_MAIN = Hero[0];
const HERO_APP = Hero[1];
const SHOW_CATEGORY_MEDIA = true;

/* ═══════════════════════════════════════════════════════════
   PALETTES
   C     — Heartlas landing (logged out). Same keys as before,
           but every value now comes from the Atlas/HEART brand
           (deep crimson red, warm gold, espresso dark, cream).
   HEART — Heartlas app shell (logged in), matches AtlasHome +
           a few cuter touches layered on top
═══════════════════════════════════════════════════════════ */
const C = {
  primary: "#C8202E",   // HEART.red
  dark: "#1B0E06",      // HEART.dark
  white: "#ffffff",
  offwhite: "#FBF6EE",  // HEART.bg
  muted: "#7A6653",     // HEART.textMuted
  border: "#EEDFC4",    // HEART.border
  gold: "#F2A93E",      // HEART.gold
  deep: "#9E1521",      // HEART.redDark — replaces the old Areluxe green
  cream: "#FCEFD4",     // HEART.goldSoft
};

const HEART = {
  bg: "#FBF6EE",
  panelBg: "#FFFFFF",
  headerBg: "#FFFFFF",
  dark: "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",
  red: "#C8202E",
  redDark: "#9E1521",
  redSoft: "#FBE7E9",
  gold: "#F2A93E",
  goldDark: "#D4900F",
  goldSoft: "#FCEFD4",
  blushSoft: "#FDEEF0",
  border: "#EEDFC4",
  divider: "#F3E6CE",
  danger: "#C8202E",
};

const SIDEBAR_W = 260;
const SIDEBAR_W_COLLAPSED = 68;

/* ═══════════════════════════════════════════════════════════
   CSS — landing (Heartlas) + shell (Heartlas, cuter)
═══════════════════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&family=DM+Sans:wght@400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
  @keyframes aeSpin { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
  @keyframes aeShimmer { 0% { background-position: 0% 50%; } 100% { background-position: 200% 50%; } }
  @keyframes hcPop{from{opacity:0;transform:translateY(10px) scale(.97);}to{opacity:1;transform:translateY(0) scale(1);}}
  @keyframes hcWiggle{0%,100%{transform:rotate(-2deg);}50%{transform:rotate(2deg);}}
  @keyframes hcBob{0%,100%{transform:translateY(0);}50%{transform:translateY(-4px);}}
@keyframes floatApp { 0%,100%{transform:translateY(0);} 50%{transform:translateY(-12px);} }


  /* ════════ LANDING (Heartlas) ════════ */
  .hc-landing { font-family: 'DM Sans', sans-serif; }
  .navbar {
    position: relative; width: 100%; height: 64px;
    display: flex; align-items: center; justify-content: space-between; padding: 0 22px;
    background: rgba(255,255,255,0.9); backdrop-filter: blur(20px) saturate(180%);
    -webkit-backdrop-filter: blur(20px) saturate(180%);
    border-radius: 20px; box-shadow: 0 6px 36px rgba(27,14,6,0.12), 0 1px 0 rgba(255,255,255,0.8) inset;
  }
  .navbar::before {
    content: ''; position: absolute; inset: -3px; border-radius: 23px;
    background: linear-gradient(90deg, ${HEART.dark}, ${HEART.gold}, ${HEART.goldDark}, ${HEART.red}, ${HEART.gold}, ${HEART.dark});
    background-size: 300% 100%; animation: aeSpin 4s linear infinite; z-index: -1;
  }
  .navbar::after {
    content: ''; position: absolute; inset: 3px; border-radius: 17px;
    background: rgba(255,255,255,0.96); backdrop-filter: blur(20px); z-index: -1;
  }
  .nav-links { display: flex; align-items: center; gap: 2px; }
  .nav-link-item {
    font-family: 'Sora', sans-serif; font-size: 14px; font-weight: 700; color: ${HEART.dark};
    background: none; border: none; padding: 9px 16px; border-radius: 10px;
    cursor: pointer; transition: background 0.15s, color 0.15s; display: flex; align-items: center; gap: 4px;
  }
  .nav-link-item:hover { background: ${HEART.goldSoft}; color: ${HEART.redDark}; }
  .nav-dropdown-wrap { position: relative; }
  .nav-dropdown-panel {
    position: absolute; top: calc(100% + 12px); left: 50%; transform: translateX(-50%);
    background: #fff; border-radius: 20px; box-shadow: 0 24px 64px rgba(27,14,6,0.2);
    border: 3px solid ${HEART.dark}; padding: 18px; z-index: 1100;
  }
  .btn-primary {
    display: inline-flex; align-items: center; gap: 8px;
    background: ${HEART.red}; color: #fff; border: 3px solid ${HEART.dark}; border-radius: 14px; padding: 14px 26px;
    font-family: 'Sora', sans-serif; font-size: 14px; font-weight: 800; cursor: pointer;
    transition: background 0.2s, transform 0.15s; white-space: nowrap;
  }
  .btn-primary:hover { background: ${HEART.redDark}; transform: translateY(-2px); }
  .btn-outline {
    display: inline-flex; align-items: center; gap: 8px;
    background: transparent; color: ${HEART.dark}; border: 3px solid ${HEART.dark}; border-radius: 14px;
    padding: 12px 24px; font-family: 'Sora', sans-serif; font-size: 14px; font-weight: 800;
    cursor: pointer; transition: background 0.2s, color 0.2s; white-space: nowrap;
  }
  .btn-outline:hover { background: ${HEART.dark}; color: #fff; }
  .mobile-menu-overlay {
    position: fixed !important; inset: 0 !important; background: #ffffff !important; z-index: 9999 !important;
    display: flex !important; flex-direction: column !important; padding: 24px !important;
  }
  @media (max-width: 900px) {
    .uih-desktop-nav { display:none !important; }
    .uih-hero-title  { font-size:40px !important; }
    .uih-search-row  { flex-direction:column !important; align-items:stretch !important; }
    .join-network-grid { grid-template-columns: 1fr !important; }
    .faq-grid { grid-template-columns: 1fr !important; minHeight: auto !important; }
    .faq-left-col { border-right: none !important; border-bottom: 3px solid ${HEART.dark} !important; }
    .book-stay-row { flex-direction: column !important; text-align: center !important; }
  }
  @media (min-width: 901px) { .uih-hamburger { display:none !important; } }

  /* ════════ SHELL (Heartlas — cuter Atlas) ════════ */
  .hc-root{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif;background:${HEART.bg};min-height:100vh;color:${HEART.dark};}
  .hc-root ::-webkit-scrollbar{width:8px;height:8px;}
  .hc-root ::-webkit-scrollbar-thumb{background:${HEART.border};border-radius:8px;}
  .hc-root button{font-family:inherit;}
  .hc-root :focus-visible{outline:2px solid ${HEART.gold};outline-offset:2px;}

  .hc-navitem{
    display:flex;align-items:center;gap:12px;width:100%;background:transparent;border:none;
    cursor:pointer;padding:10px 12px;border-radius:14px;color:${HEART.dark};font-weight:600;
    font-size:15px;text-align:left;transition:background .15s,color .15s,box-shadow .15s,transform .12s;
  }
  .hc-navitem:hover{background:${HEART.goldSoft};transform:translateX(2px);}
  .hc-navitem.active{
    background:linear-gradient(135deg, ${HEART.red}, ${HEART.redDark});
    color:#fff;box-shadow:0 6px 16px rgba(200,32,46,.35);
  }
  .hc-navitem.collapsed{justify-content:center;padding:10px;}
  .hc-navicon{
    width:36px;height:36px;border-radius:14px;background:${HEART.goldSoft};display:flex;
    align-items:center;justify-content:center;flex-shrink:0;color:${HEART.red};transition:background .15s,color .15s,transform .15s;
  }
  .hc-navitem:hover .hc-navicon{transform:rotate(-6deg) scale(1.06);}
  .hc-navitem.active .hc-navicon{background:#fff;color:${HEART.red};transform:none;}
  .hc-navlabel{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}

  .hc-subitem{
    display:flex;align-items:center;gap:10px;width:100%;background:transparent;border:none;
    cursor:pointer;padding:8px 10px 8px 30px;border-radius:12px;color:${HEART.textMuted};
    font-weight:600;font-size:13.5px;text-align:left;transition:background .15s,color .15s,transform .12s;
  }
  .hc-subitem:hover{background:${HEART.goldSoft};color:${HEART.dark};transform:translateX(2px);}
  .hc-subitem.active{background:${HEART.gold};color:${HEART.dark};font-weight:800;}
  .hc-subitem.ghost{opacity:.42;cursor:not-allowed;}
  .hc-subitem.ghost:hover{background:transparent;transform:none;color:${HEART.textMuted};}

  .hc-logout{
    display:flex;align-items:center;gap:12px;width:100%;background:transparent;border:none;
    cursor:pointer;padding:10px 12px;border-radius:14px;color:${HEART.danger};font-weight:700;
    font-size:14.5px;text-align:left;transition:background .15s;
  }
  .hc-logout:hover{background:${HEART.redSoft};}
  .hc-logout.collapsed{justify-content:center;padding:10px;}
  .hc-support{
    display:flex;align-items:center;gap:12px;width:100%;background:transparent;border:none;
    cursor:pointer;padding:10px 12px;border-radius:14px;color:${HEART.dark};font-weight:700;
    font-size:14.5px;text-align:left;transition:background .15s;
  }
  .hc-support:hover{background:${HEART.goldSoft};}
  .hc-support.collapsed{justify-content:center;padding:10px;}

  .hc-collapse-toggle{
    display:flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:12px;
    background:${HEART.goldSoft};border:none;color:${HEART.redDark};cursor:pointer;flex-shrink:0;
    transition:background .15s,color .15s;
  }
  .hc-collapse-toggle:hover{background:${HEART.gold};color:#fff;}

  .hc-tabbar{
    position:fixed;left:0;right:0;bottom:0;display:flex;background:${HEART.panelBg};
    border-top:1px solid ${HEART.border};z-index:40;padding-bottom:env(safe-area-inset-bottom);
    box-shadow:0 -4px 16px rgba(27,14,6,0.06);
  }
  .hc-tab{
    flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;
    gap:2px;padding:8px 4px 10px;background:transparent;border:none;cursor:pointer;color:${HEART.textFaint};
    transition:color .15s;width:100%;position:relative;
  }
  .hc-tab.active{color:${HEART.red};}
  .hc-tab.active::after{
    content:"";position:absolute;top:2px;width:18px;height:3px;border-radius:99px;
    background:linear-gradient(90deg, ${HEART.red}, ${HEART.gold});
  }
  .hc-tab span{font-size:11px;font-weight:700;}

  .hc-sheet-card{
    display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;
    aspect-ratio:1/1;background:${HEART.panelBg};border:1.5px solid ${HEART.border};border-radius:18px;
    cursor:pointer;padding:10px;transition:transform .1s,box-shadow .1s,border-color .1s;
  }
  .hc-sheet-card:active{transform:scale(0.96);}
  .hc-sheet-card:hover{box-shadow:0 6px 18px rgba(27,14,6,0.10);border-color:${HEART.gold};}

  .hc-backbtn{
    display:inline-flex;align-items:center;gap:6px;
    background:linear-gradient(135deg, ${HEART.red}, ${HEART.redDark});color:#fff;
    border:none;border-radius:999px;padding:12px 26px;font-weight:800;font-size:14px;cursor:pointer;
    transition:filter .15s;box-shadow:0 8px 20px rgba(200,32,46,.32);
  }
  .hc-backbtn:hover{filter:brightness(1.06);}

  .hc-fab{
    position:fixed;right:20px;bottom:20px;width:54px;height:54px;border-radius:50%;
    background:linear-gradient(135deg, ${HEART.red}, ${HEART.redDark});color:#fff;border:none;
    display:flex;align-items:center;justify-content:center;overflow:hidden;
    box-shadow:0 6px 18px rgba(200,32,46,0.45);cursor:pointer;z-index:50;transition:filter .15s,transform .15s;
  }
  .hc-fab:hover{filter:brightness(1.08);transform:translateY(-2px);}

  /* cuter showcase cards — pastel icon bubble tinted per category */
  .hc-showcase-card{
    position:relative;display:flex;flex-direction:column;align-items:center;justify-content:center;
    gap:10px;background:#fff;border:2px solid ${HEART.border};border-radius:22px;
    padding:22px 14px;cursor:pointer;transition:transform .16s,box-shadow .16s,border-color .16s;
    animation:hcPop .35s ease both;overflow:hidden;
  }
  .hc-showcase-card::before{
    content:"";position:absolute;top:-24px;right:-24px;width:64px;height:64px;border-radius:50%;
    background:${HEART.goldSoft};opacity:.7;transition:transform .2s;
  }
  .hc-showcase-card:hover{transform:translateY(-5px) rotate(-.5deg);box-shadow:0 14px 30px rgba(200,32,46,0.14);border-color:${HEART.red};}
  .hc-showcase-card:hover::before{transform:scale(1.3);}
  .hc-showcase-iconwrap{
    width:58px;height:58px;border-radius:20px;
    background:linear-gradient(135deg, ${HEART.red}, ${HEART.redDark});
    color:#fff;display:flex;align-items:center;justify-content:center;
    box-shadow:0 6px 14px rgba(200,32,46,0.3);position:relative;z-index:1;
    transition:transform .18s;
  }
  .hc-showcase-card:hover .hc-showcase-iconwrap{transform:rotate(6deg) scale(1.06);animation:hcBob 1.4s ease-in-out infinite;}
  .hc-showcase-label{font-size:13.5px;font-weight:800;color:${HEART.dark};text-align:center;position:relative;z-index:1;}

  .hc-drawer-item{
    display:flex;align-items:center;gap:12px;width:100%;background:transparent;border:none;
    cursor:pointer;padding:14px 10px;border-radius:14px;color:${HEART.dark};
    font-weight:900;font-size:16.5px;text-align:left;letter-spacing:-.2px;
    transition:background .15s,transform .1s;
  }
  .hc-drawer-item:hover{background:${HEART.goldSoft};transform:translateX(3px);}
  .hc-login-btn{
    margin-top:auto;display:flex;align-items:center;justify-content:center;gap:8px;
    width:100%;padding:15px;border-radius:12px;border:none;cursor:pointer;
    background:${HEART.red};color:#fff;font-weight:900;font-size:15.5px;letter-spacing:.02em;
    box-shadow:0 8px 20px rgba(200,32,46,.35);transition:filter .15s,transform .1s;
  }
  .hc-login-btn:hover{filter:brightness(1.08);transform:translateY(-1px);}
  .hc-logo-wiggle{animation:hcWiggle 2.6s ease-in-out infinite;transform-origin:center;}

  @media (prefers-reduced-motion: reduce){ .hc-root *, .hc-landing * { animation:none !important; transition:none !important; } }
`;

/* ═══════════════════════════════════════════════════════════
   SHARED HELPERS (landing)
═══════════════════════════════════════════════════════════ */
const AeRing: React.FC<{ children: React.ReactNode; r?: number }> = ({ children, r = 12 }) => (
  <div style={{ position: "relative", display: "inline-flex", borderRadius: r }}>
    <div style={{
      position: "absolute", inset: -3, borderRadius: r + 3,
      background: `linear-gradient(90deg, ${HEART.dark}, ${HEART.gold}, ${HEART.goldDark}, ${HEART.red}, ${HEART.gold}, ${HEART.dark})`,
      backgroundSize: "300% 100%", animation: "aeSpin 3s linear infinite",
    }} />
    <div style={{ position: "relative", background: "#fff", borderRadius: r - 1, padding: 2, display: "inline-flex" }}>
      {children}
    </div>
  </div>
);

const darken = (hex: string, amount = 0.55): string => {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
  const num = parseInt(full, 16);
  if (Number.isNaN(num)) return HEART.dark;
  const r = Math.round(((num >> 16) & 255) * (1 - amount));
  const g = Math.round(((num >> 8) & 255) * (1 - amount));
  const b = Math.round((num & 255) * (1 - amount));
  return `#${[r, g, b].map(v => v.toString(16).padStart(2, "0")).join("")}`;
};

/* ═══ visual: video → image → icon fallback + Heartlas watermark ═══ */
const CategoryVisual: React.FC<{ category: UICategoryItem; textColor: string }> = ({ category, textColor }) => {
  const [failed, setFailed] = useState(false);
  const hasVideo = SHOW_CATEGORY_MEDIA && !!category.video && !failed;
  const hasImage = SHOW_CATEGORY_MEDIA && !hasVideo && !!category.image && !failed;

  const Watermark = () => (
    <div style={{ position: "absolute", right: 12, bottom: 12, display: "flex", alignItems: "center", gap: 6, zIndex: 10, background: "rgba(27,14,6,0.35)", padding: "4px 8px", borderRadius: 8, pointerEvents: "none" }}>
      <img src={logoImg} alt="Heartlas" style={{ width: 12, height: 12, borderRadius: 2, objectFit: "cover" }} />
      <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", fontStyle: "italic" }}>Heartlas</span>
    </div>
  );

  if (hasVideo) {
    return (
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        <video src={category.video} autoPlay muted loop playsInline onError={() => setFailed(true)}
          style={{ position: "absolute", right: -8, bottom: -8, width: "48%", maxWidth: 230, borderRadius: 16, objectFit: "cover", pointerEvents: "none" }} />
        <Watermark />
      </div>
    );
  }
  if (hasImage) {
    return (
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        <img src={category.image} alt={category.name} onError={() => setFailed(true)}
          style={{ position: "absolute", right: -8, bottom: -8, width: "48%", maxWidth: 230, objectFit: "contain", pointerEvents: "none" }} />
        <Watermark />
      </div>
    );
  }
  return (
    <div style={{ position: "absolute", right: 20, bottom: 16, opacity: 0.14, color: textColor, transform: "scale(3)" }}>
      {category.icon}
    </div>
  );
};

/* ═══ INTRO SHOWCASE — full white takeover before the landing ═══ */
const INTRO_SLIDE_MS = 6000;

const IntroShowcase: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const items = APP_CATEGORIES.filter(c => !!c.image);
  const [index, setIndex] = useState(0);
  const [exiting, setExiting] = useState(false);

  const finish = () => {
    if (exiting) return;
    setExiting(true);
    setTimeout(onDone, 700);
  };

  useEffect(() => {
    if (exiting || items.length === 0) return;
    const t = setTimeout(() => {
      if (index === items.length - 1) finish();
      else setIndex(i => i + 1);
    }, INTRO_SLIDE_MS);
    return () => clearTimeout(t);
  }, [index, exiting, items.length]); // eslint-disable-line

  useEffect(() => { if (items.length === 0) onDone(); }, [items.length, onDone]);

  if (items.length === 0) return null;
  const current = items[index];

  return (
    <motion.div
      initial={{ opacity: 1 }}
      animate={exiting ? { opacity: 0, filter: "blur(30px)" } : { opacity: 1, filter: "blur(0px)" }}
      transition={{ duration: 0.7, ease: "easeInOut" }}
      style={{
        position: "fixed", inset: 0, zIndex: 5000, background: "#ffffff",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        overflow: "hidden", pointerEvents: exiting ? "none" : "auto",
      }}
    >
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: "linear-gradient(100deg, transparent 20%, rgba(200,32,46,0.10) 35%, rgba(242,169,62,0.12) 50%, rgba(212,144,15,0.10) 65%, transparent 80%)",
        backgroundSize: "300% 300%", animation: "aeShimmer 3.4s ease-in-out infinite",
      }} />
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gridTemplateRows: "repeat(5, 1fr)" }}>
        {Array.from({ length: 30 }).map((_, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, transform: i % 2 === 0 ? "rotate(-18deg)" : "rotate(-18deg) translateY(10px)", opacity: 0.05 }}>
            <img src={logoImg} alt="" style={{ width: 22, height: 22, borderRadius: 5, objectFit: "cover" }} />
            <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 15, fontStyle: "italic", color: HEART.dark, whiteSpace: "nowrap" }}>Heartlas</span>
          </div>
        ))}
      </div>

      <button onClick={finish}
        style={{ position: "absolute", top: 26, right: 26, zIndex: 2, background: "none", border: `2px solid ${HEART.dark}`, borderRadius: 999, padding: "9px 20px", fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 12.5, letterSpacing: "0.03em", color: HEART.dark, cursor: "pointer" }}>
        Skip
      </button>

      <AnimatePresence mode="wait">
        <motion.div key={current.id}
          initial={{ opacity: 0, y: 22, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          exit={{ opacity: 0, y: -22, filter: "blur(8px)" }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", maxWidth: 860, padding: "0 24px", position: "relative", zIndex: 1 }}
        >
          <img src={logoImg} alt="Heartlas" style={{ width: 88, height: 88, borderRadius: 20, objectFit: "cover", marginBottom: 26, boxShadow: "0 14px 40px rgba(27,14,6,0.18)" }} />
          <h1 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 52, letterSpacing: "-0.03em", color: HEART.dark, marginBottom: 26, textShadow: "0 1px 0 #fff, 0 2px 0 #F3E6CE, 0 3px 0 #EEDFC4, 0 4px 6px rgba(27,14,6,0.18)" }}>
            {current.name}
          </h1>
          {current.image && (
            <div style={{ position: "relative", marginBottom: 30 }}>
              <img src={current.image} alt={current.name} style={{ width: "min(84vw, 640px)", maxHeight: "56vh", objectFit: "contain", borderRadius: 24 }} />
              <div style={{ position: "absolute", right: 14, bottom: 14, display: "flex", alignItems: "center", gap: 6, background: "rgba(27,14,6,0.55)", padding: "5px 10px", borderRadius: 8 }}>
                <img src={logoImg} alt="Heartlas" style={{ width: 14, height: 14, borderRadius: 3, objectFit: "cover" }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", fontStyle: "italic" }}>Heartlas</span>
              </div>
            </div>
          )}
          {current.description && (
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 18, fontWeight: 600, color: "#3A2A1B", lineHeight: 1.7, maxWidth: 520, textShadow: "0 1px 0 #fff, 0 2px 3px rgba(27,14,6,0.10)" }}>
              {current.description}
            </p>
          )}
        </motion.div>
      </AnimatePresence>

      <div style={{ position: "absolute", bottom: 40, display: "flex", gap: 8, zIndex: 1 }}>
        {items.map((it, i) => (
          <span key={it.id} style={{ width: i === index ? 22 : 7, height: 7, borderRadius: 999, background: i === index ? HEART.red : HEART.border, transition: "all 0.3s" }} />
        ))}
      </div>
    </motion.div>
  );
};

/* ═══ CATEGORY STRIP — scroll-driven chowdeck expander ═══ */
const CategoryStrip: React.FC = () => {
  const items = APP_CATEGORIES;
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start 0.8", "end 0.35"] });
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const idx = Math.min(items.length - 1, Math.max(0, Math.floor(v * items.length)));
    setActiveIndex(idx);
  });

  const handleCardClick = (catName: string) => {
    navigate(PRODUCT_ROUTES[catName] || "/vacationrentals");
  };

  return (
    <div ref={sectionRef} style={{ display: "flex", gap: 14, height: 440, width: "100%", cursor: "pointer" }}>
      {items.map((cat, i) => {
        const isActive = i === activeIndex;
        const textColor = darken(cat.color ?? HEART.border);
        return (
          <motion.div key={cat.id} layout transition={{ type: "spring", stiffness: 210, damping: 28 }}
            onClick={() => handleCardClick(cat.name)}
            style={{ flex: isActive ? 3.4 : 1, minWidth: isActive ? undefined : 78, background: cat.color, borderRadius: 24, border: `3px solid ${HEART.dark}`, position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 24, left: 24, color: textColor }}>{cat.icon}</div>
            <AnimatePresence mode="wait">
              {isActive ? (
                <motion.div key="expanded" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2, delay: 0.1 }}
                  style={{ position: "absolute", top: 76, left: 24, right: 24, bottom: 24 }}>
                  <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 28, color: textColor, marginBottom: 10, letterSpacing: "-0.02em" }}>{cat.name}</h3>
                  {cat.description && (
                    <p style={{ fontSize: 13, lineHeight: 1.7, color: textColor, opacity: 0.82, maxWidth: "58%", fontWeight: 500 }}>{cat.description}</p>
                  )}
                  <CategoryVisual category={cat} textColor={textColor} />
                </motion.div>
              ) : (
                <motion.div key="collapsed" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}
                  style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 17, color: textColor }}>{cat.name}</span>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
};

/* ═══ NETWORK CARD ═══ */
const NetworkCard: React.FC<{ item: UICategoryItem }> = ({ item }) => {
  const textColor = darken(item.color ?? HEART.border, 0.75);
  return (
    <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      style={{ background: "#fff", border: `3px solid ${HEART.dark}`, borderRadius: 22, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "26px 26px 0" }}>
        <div style={{ color: darken(item.color ?? HEART.border, 0.35), marginBottom: 14 }}>{item.icon}</div>
        <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 23, fontWeight: 900, color: C.dark, marginBottom: 10 }}>{item.name}</h3>
        {item.description && (
          <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.7, marginBottom: 18, fontWeight: 500 }}>{item.description}</p>
        )}
        <button style={{ background: "none", border: "none", padding: 0, marginBottom: 20, display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 12, letterSpacing: "0.08em", color: C.dark, cursor: "pointer", textTransform: "uppercase" as const }}>
          See more <ArrowRight size={13} />
        </button>
      </div>
      <div style={{ position: "relative", height: 150, background: item.color }}>
        <div style={{ position: "absolute", right: 10, bottom: -10, opacity: 0.25, color: textColor, transform: "scale(3.4)" }}>{item.icon}</div>
      </div>
    </motion.div>
  );
};

/* ═══ FAQ SECTION ═══ */
const FAQSection: React.FC = () => {
  const [activeId, setActiveId] = useState(FAQ_ITEMS[0]?.id);
  const active: FAQItem | undefined = FAQ_ITEMS.find(f => f.id === activeId);
  return (
    <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className="faq-grid"
      style={{ background: "#fff", border: `4px solid ${HEART.dark}`, borderRadius: 32, padding: 0, maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0, overflow: "hidden", minHeight: 480 }}>
      <div style={{ display: "flex", flexDirection: "column", borderRight: `3px solid ${HEART.dark}`, padding: "36px 28px" }} className="faq-left-col">
        <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 28, fontWeight: 900, color: HEART.red, marginBottom: 24, letterSpacing: "-0.01em", flexShrink: 0 }}>FAQs.</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, overflowY: "auto", paddingRight: 8, flexGrow: 1 }}>
          {FAQ_ITEMS.map(f => {
            const isActive = f.id === activeId;
            return (
              <button key={f.id} onClick={() => setActiveId(f.id)}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", textAlign: "left", padding: "14px 16px", borderRadius: 12, cursor: "pointer", fontFamily: "'Sora',sans-serif", fontSize: 13.5, fontWeight: 700, background: isActive ? HEART.redSoft : HEART.bg, color: isActive ? HEART.redDark : C.dark, border: isActive ? `3px solid ${HEART.red}` : `2px solid ${HEART.border}`, transition: "all 0.15s", flexShrink: 0 }}>
                {f.question}
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", padding: "36px 28px" }}>
        <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 28, fontWeight: 900, color: HEART.red, marginBottom: 24, letterSpacing: "-0.01em", flexShrink: 0 }}>Ans.</h3>
        <AnimatePresence mode="wait">
          {active && (
            <motion.div key={active.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}
              style={{ background: C.gold, border: `3px solid ${HEART.dark}`, borderRadius: 18, padding: "28px 26px", flex: 1, display: "flex", flexDirection: "column" }}>
              <div style={{ color: HEART.dark, marginBottom: 16, fontSize: 20, flexShrink: 0 }}>{active.icon}</div>
              <p style={{ fontSize: 14.5, lineHeight: 1.9, color: "#4A2E08", fontWeight: 600, margin: 0 }}>{active.answer}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

/* ═══ BOOK YOUR STAY / APP DOWNLOAD CARD ═══ */
const APP_SLIDE_MS = 3000;

const BookYourStayCard: React.FC<{ heroItem: UICategoryItem }> = ({ heroItem }) => {
  const [failed, setFailed] = useState<Record<number, boolean>>({});
  const [index, setIndex] = useState(0);

  const shots = useMemo(() => {
    const all = heroItem.images ?? (heroItem.image ? [heroItem.image] : []);
    return all.filter((_, i) => !failed[i]);
  }, [heroItem, failed]);

  useEffect(() => {
    if (shots.length < 2) return;
    const t = setInterval(() => setIndex(i => (i + 1) % shots.length), APP_SLIDE_MS);
    return () => clearInterval(t);
  }, [shots.length]);

  useEffect(() => { if (index >= shots.length) setIndex(0); }, [shots.length, index]);

  const handleEnter = (e: React.MouseEvent<HTMLDivElement>) => { e.currentTarget.style.transform = "scale(1.05)"; };
  const handleLeave = (e: React.MouseEvent<HTMLDivElement>) => { e.currentTarget.style.transform = "scale(1)"; };

  return (
    <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className="book-stay-row"
      style={{ display: "flex", alignItems: "center", gap: 40, background: `linear-gradient(135deg, ${C.deep}, ${HEART.red})`, border: `3px solid ${HEART.dark}`, borderRadius: 24, padding: "44px 44px", maxWidth: 1000, margin: "0 auto", overflow: "hidden", position: "relative" }}>
      <div style={{ flex: 1, position: "relative", zIndex: 1 }}>
        <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 38, color: "#fff", lineHeight: 1.1, letterSpacing: "-0.02em" }}>
          Download on Play Store<br />and App Store
        </h2>
      </div>

      <div style={{ flexShrink: 0, position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 20 }}>
        {shots.length > 0 ? (
          <>
            <div style={{ position: "relative", display: "inline-block", animation: "floatApp 4s ease-in-out infinite" }}>
              {/* gradient ring */}
              <div style={{ position: "absolute", inset: -6, borderRadius: 46, background: `linear-gradient(90deg, ${HEART.dark}, ${HEART.gold}, ${HEART.goldDark}, ${HEART.red}, ${HEART.gold}, ${HEART.dark})`, backgroundSize: "300% 100%", animation: "aeSpin 3s linear infinite", zIndex: 0 }} />
              {/* phone shell */}
              <div style={{ position: "relative", zIndex: 1, background: "rgba(27,14,6,0.6)", borderRadius: 42, padding: 4, width: 268, height: 480 }}>
                <AnimatePresence mode="wait">
                  <motion.img
                    key={shots[index]}
                    src={shots[index]}
                    alt={heroItem.name}
                    onError={() => setFailed(f => ({ ...f, [index]: true }))}
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.03 }}
                    transition={{ duration: 0.45, ease: "easeInOut" }}
                    style={{ position: "absolute", inset: 4, width: "calc(100% - 8px)", height: "calc(100% - 8px)", objectFit: "contain", borderRadius: 38, display: "block", filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.5))" }}
                  />
                </AnimatePresence>
              </div>
            </div>

            {shots.length > 1 && (
              <div style={{ display: "flex", gap: 7 }}>
                {shots.map((s, i) => (
                  <button key={s} onClick={() => setIndex(i)} aria-label={`Screenshot ${i + 1}`}
                    style={{ width: i === index ? 20 : 7, height: 7, borderRadius: 999, border: "none", padding: 0, cursor: "pointer", background: i === index ? "#fff" : "rgba(255,255,255,0.4)", transition: "all 0.3s" }} />
                ))}
              </div>
            )}
          </>
        ) : (
          <div style={{ width: 268, height: 420, borderRadius: 42, background: "rgba(255,255,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", border: "3px solid rgba(255,255,255,0.3)" }}>
            {heroItem.icon ? React.cloneElement(heroItem.icon as React.ReactElement<any>, { size: 56, color: "#fff", strokeWidth: 1.6 }) : <Smartphone size={56} color="#fff" strokeWidth={1.6} />}
          </div>
        )}

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", justifyContent: "center" }}>
          <div role="button" onClick={() => window.open("https://play.google.com/store/apps/details?id=com.heartlas.android", "_blank")} onMouseEnter={handleEnter} onMouseLeave={handleLeave}
            style={{ display: "inline-block", cursor: "pointer", transition: "transform 0.2s" }}>
            <img src={GoogleLogo} alt="Google Play" style={{ height: 48, borderRadius: 12, border: "2px solid rgba(255,255,255,0.3)", padding: 6, background: "rgba(255,255,255,0.1)" }} />
          </div>
          <div role="button" onClick={() => window.open("https://apps.apple.com/app/com.heartlas.ios", "_blank")} onMouseEnter={handleEnter} onMouseLeave={handleLeave}
            style={{ display: "inline-block", cursor: "pointer", transition: "transform 0.2s" }}>
            <img src={AppleLogo} alt="App Store" style={{ height: 48, borderRadius: 12, border: "2px solid rgba(255,255,255,0.3)", padding: 6, background: "rgba(255,255,255,0.1)" }} />
          </div>
        </div>
      </div>

      <div style={{ position: "absolute", right: -40, bottom: -50, width: 220, height: 220, borderRadius: "50%", background: "rgba(255,255,255,0.06)", pointerEvents: "none" }} />
    </motion.div>
  );
};



/* ═══════════════════════════════════════════════════════════
   LOGGED-OUT LANDING — Heartlas, in the Atlas brand colors
═══════════════════════════════════════════════════════════ */
const LandingView: React.FC = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showIntro, setShowIntro] = useState(true);

  const taglines = ["Where to next?", "Ready to check in?", "Your next stay awaits."];
  const [tagIdx, setTagIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTagIdx(i => (i + 1) % taglines.length), 3200);
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  const onHost = () => navigate("/authswitch");
  const onSearch = () => go("vacationrentals-nav");
  const go = (id: string) => {
    setMenuOpen(false);
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 60);
  };

  return (
    <div className="hc-landing" style={{ minHeight: "100vh", background: C.white, color: C.dark, overflowX: "hidden" }}>
      <AnimatePresence>
        {showIntro && <IntroShowcase onDone={() => setShowIntro(false)} />}
      </AnimatePresence>

      {/* ── FLOATING NAVBAR ── */}
      <div style={{ position: "fixed", top: 16, left: 20, right: 20, margin: "0 auto", maxWidth: 1080, zIndex: 1000 }}>
        <nav className="navbar">
          <div onClick={() => navigate("/")} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 10, position: "relative", zIndex: 1 }}>
            <AeRing><img src={logoImg} alt="Heartlas" style={{ width: 32, height: 32, borderRadius: 9, objectFit: "cover", display: "block" }} /></AeRing>
            <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 17 }}>Heartlas</span>
          </div>

          <div className="nav-links uih-desktop-nav" style={{ position: "relative", zIndex: 1 }}>
            <button className="nav-link-item" onClick={() => navigate("/vacationrentals")}>Stays</button>
            <button className="nav-link-item" onClick={() => navigate("/hosting")}>Hosting</button>

            <div className="nav-dropdown-wrap" onMouseEnter={() => setProductsOpen(true)} onMouseLeave={() => setProductsOpen(false)}>
              <button className="nav-link-item">Products <ChevronDown size={13} /></button>
              <AnimatePresence>
                {productsOpen && (
                  <motion.div className="nav-dropdown-panel" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.15 }} style={{ width: 420 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                      {APP_CATEGORIES.map(cat => (
                        <button key={cat.id} onClick={() => navigate(PRODUCT_ROUTES[cat.name] || "/vacationrentals")}
                          style={{ display: "flex", alignItems: "center", gap: 8, background: C.offwhite, border: "2px solid transparent", borderRadius: 10, padding: "10px 12px", fontSize: 13, fontWeight: 700, color: C.dark, cursor: "pointer", textAlign: "left" }}>
                          <span style={{ color: darken(cat.color ?? HEART.border) }}>{cat.icon}</span>{cat.name}
                        </button>
                      ))}
                    </div>
                    <button className="btn-primary" style={{ width: "100%", justifyContent: "center", marginBottom: 10 }} onClick={onHost}>
                      Become a Host <ArrowRight size={14} />
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="nav-dropdown-wrap" onMouseEnter={() => setMoreOpen(true)} onMouseLeave={() => setMoreOpen(false)}>
              <button className="nav-link-item">More <ChevronDown size={13} /></button>
              <AnimatePresence>
                {moreOpen && (
                  <motion.div className="nav-dropdown-panel" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.15 }}
                    style={{ width: 180, display: "flex", flexDirection: "column", gap: 4 }}>
                    <button className="nav-link-item" style={{ width: "100%", justifyContent: "flex-start" }} onClick={() => navigate("/contact")}>Contact Us</button>
                    <button className="nav-link-item" style={{ width: "100%", justifyContent: "flex-start" }} onClick={() => go("faq")}>FAQs</button>
                    <button className="nav-link-item" style={{ width: "100%", justifyContent: "flex-start" }} onClick={() => navigate("/blog")}>Blog</button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, position: "relative", zIndex: 1 }}>
            <button className="nav-link-item uih-desktop-nav" onClick={() => navigate("/authswitch")} style={{ color: C.muted }}>Sign in</button>
            <button className="uih-hamburger" onClick={() => setMenuOpen(true)} style={{ background: "none", border: "none", cursor: "pointer" }}>
              <Menu size={22} />
            </button>
          </div>
        </nav>
      </div>

      {/* ── MOBILE MENU ── */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div className="mobile-menu-overlay" initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ duration: 0.22 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 40 }}>
              <AeRing><img src={logoImg} alt="Heartlas" style={{ width: 32, height: 32, borderRadius: 9, objectFit: "cover", display: "block" }} /></AeRing>
              <button onClick={() => setMenuOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={24} /></button>
            </div>
            {[["Vacation Rentals", "vacationrentals-nav"], ["Become a Host", "hosting"], ["Join our network", "network"], ["FAQs", "faq"]].map(([l, id]) => (
              <button key={id}
                onClick={() => { if (id === "hosting") { setMenuOpen(false); onHost(); } else { go(id); } }}
                style={{ textAlign: "left", background: "none", border: "none", borderBottom: `1px solid ${C.border}`, padding: "18px 0", fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 800, cursor: "pointer" }}>
                {l}
              </button>
            ))}
            <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
              <button className="btn-primary" style={{ width: "100%", justifyContent: "center" }} onClick={() => { setMenuOpen(false); onHost(); }}>Become a Host <ArrowRight size={14} /></button>
              <button className="btn-outline" style={{ width: "100%", justifyContent: "center" }} onClick={() => { setMenuOpen(false); navigate("/authswitch"); }}>Sign in</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div style={{ height: 16 + 64 + 24 }} />

      {/* ══ HERO ══ */}
      <section style={{
        padding: "56px 24px 56px", textAlign: "center", position: "relative", minHeight: "70vh",
        display: "flex", alignItems: "center", justifyContent: "center",
        backgroundImage: HERO_MAIN.image ? `url(${HERO_MAIN.image})` : undefined,
        backgroundSize: "cover", backgroundPosition: "center",
      }}>
        {HERO_MAIN.image && <div style={{ position: "absolute", inset: 0, background: "rgba(251,246,238,0.78)" }} />}
        <div style={{ position: "relative", zIndex: 1 }}>
          <AnimatePresence mode="wait">
            <motion.h1 key={tagIdx}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4 }}
              className="uih-hero-title"
              style={{ fontFamily: "'Sora',sans-serif", fontSize: 66, fontWeight: 900, letterSpacing: "-0.03em", margin: "0 auto 24px", maxWidth: 800 }}>
              {taglines[tagIdx]}
            </motion.h1>
          </AnimatePresence>

          <div className="uih-search-row" style={{ display: "flex", alignItems: "center", gap: 10, maxWidth: 560, margin: "0 auto" }}>
            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, border: `3px solid ${C.dark}`, borderRadius: 16, padding: "14px 18px", background: "#fff" }}>
              <MapPin size={16} color={C.muted} />
              <input placeholder="Where are you headed?" value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onSearch()}
                style={{ border: "none", outline: "none", fontSize: 14, width: "100%", fontFamily: "'DM Sans',sans-serif", fontWeight: 600 }} />
            </div>
            <button onClick={onSearch} className="btn-primary"><Search size={15} /> Search</button>
          </div>
        </div>
      </section>

      {/* ── CATEGORY STRIP ── */}
      <section id="vacationrentals-nav" style={{ padding: "20px 24px 90px", maxWidth: 1180, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 36, letterSpacing: "-0.02em", marginBottom: 8 }}>What's on Heartlas?</h2>
          <p style={{ fontSize: 15, color: C.muted, fontWeight: 600 }}>Everything you need for your next trip, in one place.</p>
        </div>
        <CategoryStrip />
      </section>

      {/* ══ JOIN OUR GROWING NETWORK ══ */}
      <section id="network" style={{ padding: "20px 24px 90px", maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 34, letterSpacing: "-0.02em" }}>Join our growing network</h2>
        </div>
        <div className="join-network-grid" style={{ display: "grid", gridTemplateColumns: `repeat(${NETWORK_CARDS.length}, 1fr)`, gap: 20 }}>
          {NETWORK_CARDS.map(item => <NetworkCard key={item.id} item={item} />)}
        </div>
      </section>

      {/* ══ FAQ ══ */}
      <section id="faq" style={{ padding: "80px 24px", background: C.dark, position: "relative" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}><FAQSection /></div>
      </section>

      {/* ══ APP DOWNLOAD ══ */}
      <section style={{ padding: "0 24px 90px", background: C.dark }}>
        <BookYourStayCard heroItem={HERO_APP} />
      </section>

      <SmartFooter />
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   CATEGORY SHOWCASE (logged-in Home hero grid) — cuter cards
═══════════════════════════════════════════════════════════ */
const CategoryShowcase: React.FC<{
  items: CategoryItem[];
  onSelect: (item: CategoryItem) => void;
  title?: string;
}> = ({ items, onSelect, title }) => {
  if (!items.length) return null;
  return (
    <div style={{ width: "100%" }}>
      {title && <div style={S.showcaseTitle}>{title}</div>}
      <div style={S.showcaseGrid}>
        {items.map((item, i) => (
          <button key={item.id} className="hc-showcase-card" style={{ animationDelay: `${i * 45}ms` }} onClick={() => onSelect(item)}>
            <span className="hc-showcase-iconwrap">{renderAppCategoryVisual(item, 26)}</span>
            <span className="hc-showcase-label">{item.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN — HomeCentral
═══════════════════════════════════════════════════════════ */
const HomeCentral: React.FC = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [activeSubId, setActiveSubId] = useState<number | null>(DEFAULT_ACTIVE_SUBCATEGORY_ID);
  const [openDesktopSection, setOpenDesktopSection] = useState<number | null>(SHOPPING_CATEGORY_ID);
  const [mobileSheetParentId, setMobileSheetParentId] = useState<number | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 900);
    fn();
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!chatOpen || !isMobile) return;
    const fn = (e: MouseEvent) => {
      if (chatRef.current && !chatRef.current.contains(e.target as Node)) setChatOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [chatOpen, isMobile]);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const user = useSelector((s: RootState) => s.auth.user);
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);
  const userId = user?.userId;
  const firstName = user?.firstName || "";
  const avatarSrc = user?.profilePicture || appLogo;

  const handleLogout = async () => {
    try { await dispatch(logout()).unwrap(); }
    finally { navigate(ROUTES.signIn); }
  };

  const goHome = () => {
    setActiveSubId(null);
    setOpenDesktopSection(null);
    setMobileSheetParentId(null);
    setChatOpen(false);
  };

  const handleCategoryTap = (category: CategoryItem) => {
    if (category.id === HOME_CATEGORY_ID) { goHome(); return; }
    if (category.id === CHAT_CATEGORY_ID) {
      if (!user) { navigate(ROUTES.signIn); return; }
      setChatOpen(true);
      return;
    }
    if (category.id === ORDERS_CATEGORY_ID && !user) { navigate(ROUTES.signIn); return; }

    if (isMobile) {
      setMobileSheetParentId(prev => (prev === category.id ? null : category.id));
      return;
    }
    if (!sidebarOpen) {
      setSidebarOpen(true);
      setOpenDesktopSection(category.id);
      return;
    }
    setOpenDesktopSection(prev => (prev === category.id ? null : category.id));
  };

  const handleSubcategoryTap = (sub: CategoryItem, parentCategoryId: number) => {
    // Screens without a wired component (still // commented) do nothing —
    // they render dimmed so the toggle zone is obvious in dev.
    if (!SCREEN_COMPONENTS[sub.id]) return;
    setActiveSubId(sub.id);
    setChatOpen(false);
    setOpenDesktopSection(parentCategoryId === SHOPPING_CATEGORY_ID ? SHOPPING_CATEGORY_ID : null);
    setMobileSheetParentId(null);
  };

  const ActiveComponent: React.FC<any> | null =
    activeSubId != null ? SCREEN_COMPONENTS[activeSubId] ?? null : null;

  const nonHomeCategories = useMemo(() => appCategories.filter(c => c.id !== HOME_CATEGORY_ID), []);
  const homeCategoryItem = useMemo(() => appCategories.find(c => c.id === HOME_CATEGORY_ID) ?? null, []);
  const showcaseCategories = useMemo(
    () => nonHomeCategories.filter(c => c.id !== ORDERS_CATEGORY_ID && c.id !== CHAT_CATEGORY_ID),
    [nonHomeCategories]
  );

  const isCategoryActiveFor = (category: CategoryItem) => {
    if (category.id === HOME_CATEGORY_ID) return activeSubId == null && !chatOpen;
    if (category.id === CHAT_CATEGORY_ID) return chatOpen;
    return getAppSubcategories(category.id).some(s => s.id === activeSubId);
  };

  /* ── still checking auth ── */
  if (!authLoaded) {
    return (
      <div className="hc-root" style={S.page}>
        <style>{CSS}</style>
        <div style={S.loaderBox}><Cloud size={34} color={HEART.red} /></div>
      </div>
    );
  }

  /* ═══ LOGGED OUT — the Heartlas landing ═══ */
  if (!user) {
    return (
      <>
        <style>{CSS}</style>
        <LandingView />
      </>
    );
  }

  const currentSidebarW = sidebarOpen ? SIDEBAR_W : SIDEBAR_W_COLLAPSED;

  /* ═══ LOGGED IN — the (cuter) Heartlas shell ═══ */
  return (
    <div className="hc-root" style={S.page}>
      <style>{CSS}</style>

      {/* header — only on the Home hero view */}
      {!ActiveComponent && (
        <div style={S.header}>
          <div style={S.brandRow}>
            <div style={S.logoRingOuter}>
              <div style={S.logoRingBorder} />
              <div style={S.logoRingInner}>
                <img src={appLogo} alt="Heartlas" style={S.logoImg} className="hc-logo-wiggle"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
              </div>
            </div>
            {!isMobile && (
              <span style={S.greeting}>
                Hi, {firstName || "there"} <Heart size={13} color={HEART.red} fill={HEART.red} style={{ verticalAlign: "-2px" }} />
              </span>
            )}
          </div>

          <button style={S.profileMenuBtn} onClick={() => setMenuOpen(true)}
            onMouseDown={(e) => { e.currentTarget.style.transform = "translateY(1px)"; e.currentTarget.style.boxShadow = "0 1px 3px rgba(27,14,6,.18)"; }}
            onMouseUp={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 3px 10px rgba(27,14,6,.12)"; }}>
            <img src={avatarSrc} alt={firstName || "Profile"}
              onError={(e) => { (e.currentTarget as HTMLImageElement).onerror = null; (e.currentTarget as HTMLImageElement).src = appLogo; }}
              style={S.profileAvatar} />
            <Menu size={18} color={HEART.dark} />
          </button>
        </div>
      )}

      <div style={{ display: "flex", height: ActiveComponent ? "100vh" : "calc(100vh - 61px)", overflow: "hidden" }}>

        {/* ── desktop sidebar ── */}
        {!isMobile && (
          <div style={{ ...S.sidebar, width: currentSidebarW, top: ActiveComponent ? 0 : 61, height: ActiveComponent ? "100vh" : "calc(100vh - 61px)" }}>
            <div style={S.sidebarToggleRow}>
              {sidebarOpen && <span style={S.sidebarToggleLabel}><Sparkles size={11} style={{ verticalAlign: "-1px", marginRight: 4 }} />Menu</span>}
              <button className="hc-collapse-toggle" onClick={() => setSidebarOpen(prev => !prev)}
                aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"} title={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}>
                {sidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
              </button>
            </div>

            <div style={{ flex: 1, overflowY: "auto" }}>
              {nonHomeCategories.map((category) => {
                const subs = getAppSubcategories(category.id);
                const isChat = category.id === CHAT_CATEGORY_ID;
                const isCategoryActive = isCategoryActiveFor(category);
                return (
                  <div key={category.id} style={{ marginBottom: 4 }}>
                    <button className={`hc-navitem${isCategoryActive ? " active" : ""}${!sidebarOpen ? " collapsed" : ""}`}
                      onClick={() => handleCategoryTap(category)} title={category.name}>
                      <span className="hc-navicon">{renderAppCategoryVisual(category, 18)}</span>
                      {sidebarOpen && <span className="hc-navlabel">{category.name}</span>}
                    </button>
                    {sidebarOpen && !isChat && openDesktopSection === category.id && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 4, marginBottom: 8 }}>
                        {subs.map((sub) => {
                          const wired = !!SCREEN_COMPONENTS[sub.id];
                          return (
                            <button key={sub.id}
                              className={`hc-subitem${activeSubId === sub.id ? " active" : ""}${!wired ? " ghost" : ""}`}
                              onClick={() => handleSubcategoryTap(sub, category.id)}
                              title={wired ? sub.name : `${sub.name} — un-comment its line in SCREEN_COMPONENTS to enable`}>
                              {renderAppCategoryVisual(sub, 14)}
                              <span>{sub.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={S.sidebarFooter}>
              <button className={`hc-navitem${activeSubId == null && !chatOpen ? " active" : ""}${!sidebarOpen ? " collapsed" : ""}`} onClick={goHome} title="Home">
                <span className="hc-navicon">{homeCategoryItem ? renderAppCategoryVisual(homeCategoryItem, 18) : <HomeIcon size={18} />}</span>
                {sidebarOpen && <span className="hc-navlabel">Home</span>}
              </button>
              <button className={`hc-support${!sidebarOpen ? " collapsed" : ""}`} onClick={() => navigate(ROUTES.support(userId))} title="Support">
                <LifeBuoy size={18} color={HEART.redDark} />
                {sidebarOpen && <span>Support</span>}
              </button>
              <button className={`hc-logout${!sidebarOpen ? " collapsed" : ""}`} onClick={handleLogout} title="Log out">
                <LogOut size={18} />
                {sidebarOpen && <span>Log out</span>}
              </button>
            </div>
          </div>
        )}

        {/* ── main content ── */}
        <div style={{
          ...S.content,
          maxWidth: ActiveComponent ? "none" : S.content.maxWidth,
          padding: ActiveComponent ? 0 : S.content.padding,
          marginBottom: isMobile ? 76 : 0,
        }}>
          {ActiveComponent ? (
            ActiveComponent === VacationRentalsHome ? <VacationRentalsHome embedded /> : <ActiveComponent />
          ) : (
            <>
              <div style={S.hero}>
                <div style={S.heroTitle}>
                  Where to today, {firstName || "friend"}? <span aria-hidden="true">✨</span>
                </div>
                <p style={S.heroBlurb}>Pick a category below (or from the sidebar) to get started.</p>
              </div>
              <CategoryShowcase items={showcaseCategories} onSelect={handleCategoryTap} />
            </>
          )}
        </div>
      </div>

      {/* ── desktop chat modal ── */}
      {!isMobile && (
        <AnimatePresence>
          {chatOpen && (
            <>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setChatOpen(false)} style={S.chatBackdrop} />
              <motion.div
                initial={{ opacity: 0, scale: 0.94, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.94, y: 16 }}
                transition={{ type: "spring", stiffness: 340, damping: 30 }} style={S.chatModal}>
                <div style={S.chatModalHeader}>
                  <span style={S.chatModalTitle}>Chat</span>
                  <button onClick={() => setChatOpen(false)} style={S.chatModalClose}><X size={18} /></button>
                </div>
                <IMessagePreview />
              </motion.div>
            </>
          )}
        </AnimatePresence>
      )}

      {/* ── mobile tab bar ── */}
      {isMobile && (
        <div className="hc-tabbar">
          {appCategories.map((category) => {
            const isChat = category.id === CHAT_CATEGORY_ID;
            const isCategoryActive = mobileSheetParentId === category.id || isCategoryActiveFor(category);
            return (
              <div key={category.id} style={{ position: "relative", flex: 1, display: "flex" }}>
                <button className={`hc-tab${isCategoryActive ? " active" : ""}`} onClick={() => handleCategoryTap(category)}>
                  {renderAppCategoryVisual(category, 22)}
                  <span>{category.name}</span>
                </button>
                {isChat && (
                  <AnimatePresence>
                    {chatOpen && (
                      <>
                        <div style={S.chatMobileScrim} onClick={() => setChatOpen(false)} />
                        <motion.div ref={chatRef}
                          initial={{ opacity: 0, y: 8, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.97 }}
                          transition={{ duration: 0.15 }} style={S.chatMobilePanel}>
                          <IMessagePreview />
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── mobile bottom sheet: subcategory cards ── */}
      <AnimatePresence>
        {isMobile && mobileSheetParentId != null && (
          <>
            <motion.div key="scrim" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={S.sheetScrim} onClick={() => setMobileSheetParentId(null)} />
            <motion.div key="sheet" initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "tween", duration: 0.22 }} style={S.sheet}>
              <div style={S.sheetHandleRow}>
                <span style={S.sheetTitle}>{appCategories.find(c => c.id === mobileSheetParentId)?.name}</span>
                <button style={S.sheetCloseBtn} onClick={() => setMobileSheetParentId(null)}><X size={18} color={HEART.textMuted} /></button>
              </div>
              <div style={S.sheetGrid}>
                {getAppSubcategories(mobileSheetParentId).map((sub) => {
                  const wired = !!SCREEN_COMPONENTS[sub.id];
                  return (
                    <div key={sub.id} className="hc-sheet-card"
                      style={!wired ? { opacity: 0.42 } : undefined}
                      onClick={() => handleSubcategoryTap(sub, mobileSheetParentId as number)}>
                      {renderAppCategoryVisual(sub, 26)}
                      <span style={S.sheetCardLabel}>{sub.name}</span>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── profile/menu slide-in panel ── */}
      {menuOpen && (
        <div style={S.sidebarOverlay} onClick={() => setMenuOpen(false)}>
          <div style={S.menuPanel} onClick={(e) => e.stopPropagation()}>
            <div style={S.menuPanelHeader}>
              <img src={avatarSrc} alt={firstName || "Profile"}
                onError={(e) => { (e.currentTarget as HTMLImageElement).onerror = null; (e.currentTarget as HTMLImageElement).src = appLogo; }}
                style={S.menuPanelAvatar} />
              <div>
                <div style={S.menuPanelName}>{firstName || "Welcome"}</div>
                <div style={S.menuPanelSub}>Manage your account</div>
              </div>
            </div>
            <button style={S.closeBtn} onClick={() => setMenuOpen(false)}><X size={18} /></button>
            <div style={S.menuPanelContent}>
              <button onClick={() => navigate(ROUTES.profile(userId))} style={S.menuPanelItem}>Profile</button>
              <button onClick={() => navigate(ROUTES.support(userId))} style={S.menuPanelItem}>Support</button>
              <button onClick={handleLogout} style={S.menuPanelItemDanger}>Logout</button>
            </div>
          </div>
        </div>
      )}

      {/* ── floating profile fab ── */}
      <button className="hc-fab" onClick={() => navigate(ROUTES.profile(userId))} aria-label="Profile">
        <img src={avatarSrc} alt="" onError={(e) => { (e.currentTarget as HTMLImageElement).src = appLogo; }}
          style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
      </button>
    </div>
  );
};

export default HomeCentral;

/* ═══════════════════════════════════════════════════════════
   SHELL STYLES (logged in)
═══════════════════════════════════════════════════════════ */
const S: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", width: "100%", background: HEART.bg, color: HEART.dark, overflowX: "hidden" },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 20px", borderBottom: `1px solid ${HEART.border}`, background: HEART.headerBg,
    position: "sticky", top: 0, zIndex: 30,
  },
  brandRow: { display: "flex", alignItems: "center", gap: 12 },

  logoRingOuter: { position: "relative", display: "inline-flex", borderRadius: 12 },
  logoRingBorder: {
    position: "absolute", inset: -2, borderRadius: 14,
    background: `linear-gradient(90deg, ${HEART.dark}, ${HEART.gold}, ${HEART.red}, ${HEART.gold}, ${HEART.dark})`,
    backgroundSize: "300% 100%", animation: "aeSpin 3s linear infinite", zIndex: 0,
  },
  logoRingInner: { position: "relative", zIndex: 1, background: "#fff", borderRadius: 12, padding: 3, display: "inline-flex" },
  logoImg: { height: 34, width: 34, borderRadius: 10, objectFit: "cover", display: "block" },

  greeting: { fontSize: 14, fontWeight: 700, color: HEART.dark },

  profileMenuBtn: {
    display: "flex", alignItems: "center", gap: 8, height: 44, padding: "0 14px", minWidth: 76,
    borderRadius: 999, border: `1px solid ${HEART.border}`, background: "#fff",
    boxShadow: "0 3px 10px rgba(27,14,6,.12)", cursor: "pointer", transition: "box-shadow .2s, transform .15s",
  },
  profileAvatar: { width: 26, height: 26, borderRadius: "50%", objectFit: "cover", border: `1.5px solid ${HEART.gold}` },

  sidebar: {
    position: "sticky", alignSelf: "flex-start",
    padding: "16px 10px", display: "flex", flexDirection: "column", borderRight: `1px solid ${HEART.border}`,
    flexShrink: 0, transition: "width .18s ease", overflow: "hidden", background: HEART.bg, zIndex: 20,
  },
  sidebarToggleRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 2px 12px" },
  sidebarToggleLabel: { fontSize: 12, fontWeight: 800, color: HEART.textMuted, textTransform: "uppercase", letterSpacing: ".06em" },
  sidebarFooter: { borderTop: `1px solid ${HEART.divider}`, paddingTop: 10, marginTop: 10, display: "flex", flexDirection: "column", gap: 2 },

  content: { flex: 1, maxWidth: 980, padding: "24px 20px 60px", width: "100%", minWidth: 0, height: "100%", overflowY: "auto", overflowX: "hidden" },

  hero: {
    background: `linear-gradient(135deg, ${HEART.panelBg}, ${HEART.blushSoft} 60%, ${HEART.goldSoft})`,
    border: `1px solid ${HEART.border}`, borderRadius: 20,
    padding: "24px 22px", marginBottom: 24,
    boxShadow: "0 6px 20px rgba(27,14,6,0.05)",
  },
  heroTitle: { fontSize: 22, fontWeight: 900, color: HEART.dark, margin: 0 },
  heroBlurb: { fontSize: 13.5, color: HEART.textMuted, marginTop: 6 },

  loaderBox: { display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" },

  showcaseTitle: {
    fontSize: 13, fontWeight: 800, color: HEART.textMuted,
    textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 14, textAlign: "left",
  },
  showcaseGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 14, width: "100%",
  },

  sheetScrim: { position: "fixed", inset: 0, background: "rgba(27,14,6,0.45)", zIndex: 45 },
  sheet: {
    position: "fixed", left: 0, right: 0, bottom: 0, background: HEART.panelBg, zIndex: 46,
    borderTopLeftRadius: 22, borderTopRightRadius: 22, padding: "10px 16px calc(20px + env(safe-area-inset-bottom))",
    maxHeight: "70vh", overflowY: "auto", boxShadow: "0 -10px 30px rgba(27,14,6,.18)",
  },
  sheetHandleRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 2px 14px" },
  sheetTitle: { fontSize: 16, fontWeight: 900, color: HEART.dark },
  sheetCloseBtn: { background: HEART.goldSoft, border: "none", borderRadius: "50%", width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  sheetGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 },
  sheetCardLabel: { fontSize: 12, fontWeight: 700, color: HEART.dark, textAlign: "center" },

  sidebarOverlay: {
    position: "fixed", inset: 0, backgroundColor: "rgba(27,14,6,0.55)",
    display: "flex", justifyContent: "flex-end", zIndex: 6000,
  },
  menuPanel: {
    width: "100%", maxWidth: 360, height: "100%",
    background: `linear-gradient(160deg, ${HEART.goldSoft} 0%, #fff 42%, #fff 100%)`,
    boxShadow: "0 0 30px rgba(27,14,6,0.35)", padding: 24, position: "relative",
    display: "flex", flexDirection: "column", alignItems: "flex-start",
  },
  menuPanelHeader: { display: "flex", alignItems: "center", gap: 12, marginTop: 46, marginBottom: 4 },
  menuPanelAvatar: { width: 52, height: 52, borderRadius: "50%", objectFit: "cover", border: `2px solid ${HEART.gold}` },
  menuPanelName: { fontWeight: 900, fontSize: 16, color: HEART.dark },
  menuPanelSub: { fontSize: 12.5, color: HEART.textMuted, marginTop: 2 },
  closeBtn: {
    position: "absolute", top: 24, right: 24, borderRadius: "50%", width: 36, height: 36,
    border: `1px solid ${HEART.border}`, background: "#fff", cursor: "pointer",
  },
  menuPanelContent: { marginTop: 28, display: "flex", flexDirection: "column", gap: 12, width: "100%" },
  menuPanelItem: {
    padding: "13px 16px", borderRadius: 14, border: `1px solid ${HEART.border}`, background: "#fff",
    color: HEART.dark, fontWeight: 700, textAlign: "left", cursor: "pointer",
    transition: "transform .15s ease, box-shadow .15s ease, border-color .15s",
  },
  menuPanelItemDanger: {
    padding: "13px 16px", borderRadius: 14, border: `1px solid ${HEART.redSoft}`, background: HEART.redSoft,
    color: HEART.redDark, fontWeight: 800, textAlign: "left", cursor: "pointer",
    transition: "transform .15s ease, box-shadow .15s ease",
  },

  chatBackdrop: { position: "fixed", inset: 0, background: "rgba(27,14,6,.45)", zIndex: 1000, backdropFilter: "blur(4px)" },
  chatModal: {
    position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
    zIndex: 1001, width: "min(480px, calc(100vw - 48px))",
    maxHeight: "min(640px, calc(100vh - 80px))", overflowY: "auto",
    background: "#fff", borderRadius: 20,
    boxShadow: "0 30px 80px rgba(27,14,6,.3)", border: `1.5px solid ${HEART.border}`,
  },
  chatModalHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: `1px solid ${HEART.border}` },
  chatModalTitle: { fontSize: 15, fontWeight: 800, color: HEART.dark },
  chatModalClose: { background: "none", border: "none", cursor: "pointer", color: HEART.textMuted },
  chatMobileScrim: { position: "fixed", inset: 0, zIndex: 998 },
  chatMobilePanel: {
    position: "fixed", right: 12, bottom: 88, width: "min(360px, calc(100vw - 24px))",
    maxHeight: "60vh", overflowY: "auto", background: "#fff",
    border: `1.5px solid ${HEART.border}`, borderRadius: 16,
    boxShadow: "0 20px 50px rgba(27,14,6,.18)", zIndex: 999,
  },
};